import { Skeleton } from '@monksflow/monks-ui';
import clsx from 'clsx';
import { useMatch } from 'react-router';

import { ROUTE_PATTERNS, ROUTES } from '@/shared/config/routes';
import { LoadingRegion } from '@/shared/ui/skeletons';

import { ReelsStudioShell } from './ReelsStudioShell';
import { WIZARD_PANELS, WIZARD_STEPS_WITH_ASIDE } from './wizardPanels';
import { NUMBERED_STEPS, WIZARD_STEPS, type WizardStep } from './wizardSteps';

const COLUMN_ROWS = Array.from({ length: 3 });
const ASIDE_ROWS = Array.from({ length: 3 });
const CANVAS_CLASS = 'flex h-full min-h-0 flex-col gap-lg px-xl py-xl';

const ColumnPlaceholders = () => (
  <>
    <div className="flex min-h-0 flex-1 flex-col gap-md px-lg py-md">
      <Skeleton className="h-6 w-32 self-center" />
      {COLUMN_ROWS.map((_, index) => (
        <Skeleton key={index} className="h-14 w-full" />
      ))}
    </div>
    <div className="flex shrink-0 items-center justify-between px-lg py-md">
      <Skeleton className="h-9 w-20" />
      <Skeleton className="h-9 w-24" />
    </div>
  </>
);

const CanvasPlaceholders = () => (
  <>
    <Skeleton className="h-8 w-1/2" />
    <Skeleton className="min-h-64 w-full flex-1 rounded-lg" />
  </>
);

/** A heading strip and a few label and value rows, as the glances lay out, and the storyboard's action. */
const AsidePlaceholders = ({ hasAction }: { hasAction: boolean }) => (
  <>
    <div className="flex min-h-16 shrink-0 items-center border-b border-border px-lg py-md">
      <Skeleton className="h-5 w-28" />
    </div>
    <div className="flex min-h-0 flex-1 flex-col gap-lg p-lg">
      {ASIDE_ROWS.map((_, index) => (
        <div key={index} className="flex items-start gap-md">
          <Skeleton className="size-8 shrink-0 rounded-md" />
          <div className="flex flex-1 flex-col gap-xs">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-5 w-full" />
          </div>
        </div>
      ))}
    </div>
    {hasAction ? (
      <div
        data-testid="reels-wizard-aside-action-skeleton"
        className="shrink-0 border-t border-border p-lg"
      >
        <Skeleton className="h-9 w-full" />
      </div>
    ) : null}
  </>
);

/** Source Media's two grids, as the panel lays them out: images three across, videos two. */
const STUDIO_TILE_GRIDS = [
  { columns: 'grid-cols-3', tiles: 6 },
  { columns: 'grid-cols-2', tiles: 2 },
] as const;

/** The header card: the way back and the Studio heading, the title, and the two actions. */
const StudioHeaderPlaceholders = () => (
  <div className="flex min-h-16 items-center justify-between gap-md px-md py-sm">
    <Skeleton className="h-6 w-28" />
    <Skeleton className="h-5 w-40" />
    <div className="flex gap-xs">
      <Skeleton className="h-9 w-32" />
      <Skeleton className="h-9 w-36" />
    </div>
  </div>
);

/** Segment Details: its heading, the scene's name and duration fields, and Back to Storyboard. */
const StudioColumnPlaceholders = () => (
  <>
    <div className="flex min-h-0 flex-1 flex-col gap-md px-lg py-md">
      <Skeleton className="h-6 w-36" />
      <Skeleton className="h-5 w-16" />
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-5 w-20" />
      <Skeleton className="h-9 w-full" />
    </div>
    <div className="flex shrink-0 items-center px-lg py-md">
      <Skeleton className="h-9 w-44" />
    </div>
  </>
);

/** The video and the timeline under it. */
const StudioCanvasPlaceholders = () => (
  <div className="flex h-full min-h-0 flex-col gap-lg">
    <div className="flex min-h-0 flex-1 justify-center">
      <Skeleton className="aspect-[9/16] h-full max-w-full rounded-lg" />
    </div>
    <Skeleton className="h-36 w-full shrink-0 rounded-lg" />
  </div>
);

/** Source Media: its heading and the images and videos sections as rows of tiles. */
const StudioAsidePlaceholders = () => (
  <>
    <div className="flex min-h-16 shrink-0 items-center border-b border-border px-lg py-md">
      <Skeleton className="h-5 w-28" />
    </div>
    <div className="flex min-h-0 flex-1 flex-col gap-lg p-lg">
      {STUDIO_TILE_GRIDS.map(({ columns, tiles }) => (
        <div key={columns} className="flex flex-col gap-sm">
          <Skeleton className="h-5 w-40" />
          <div className={`grid gap-sm ${columns}`}>
            {Array.from({ length: tiles }, (_, tile) => (
              <Skeleton key={tile} className="aspect-[9/16] w-full rounded-md" />
            ))}
          </div>
        </div>
      ))}
    </div>
  </>
);

/** A step's column while its data loads, inside the wizard shell's own column. */
export const ReelsWizardColumnSkeleton = () => (
  <LoadingRegion data-testid="reels-wizard-column-skeleton" className={WIZARD_PANELS.columnBody}>
    <ColumnPlaceholders />
  </LoadingRegion>
);

/** A step's main pane while its data loads, inside the wizard shell's own pane. */
export const ReelsWizardCanvasSkeleton = () => (
  <LoadingRegion data-testid="reels-wizard-canvas-skeleton" className={CANVAS_CLASS}>
    <CanvasPlaceholders />
  </LoadingRegion>
);

/** A step's right panel while its data loads, inside the wizard shell's own aside. Only a member who can write gets the storyboard's action. */
export const ReelsWizardAsideSkeleton = ({
  step,
  canWrite,
}: {
  step: WizardStep;
  canWrite: boolean;
}) => (
  <LoadingRegion data-testid="reels-wizard-aside-skeleton" className="flex min-h-0 flex-1 flex-col">
    <AsidePlaceholders hasAction={step === 'storyboard' && canWrite} />
  </LoadingRegion>
);

/** The Studio's layout before its reel has loaded. */
export const ReelsStudioSkeleton = () => (
  <LoadingRegion data-testid="reels-studio-skeleton" className="flex min-h-0 flex-1 flex-col">
    <ReelsStudioShell
      header={<StudioHeaderPlaceholders />}
      column={<StudioColumnPlaceholders />}
      main={<StudioCanvasPlaceholders />}
      aside={<StudioAsidePlaceholders />}
    />
  </LoadingRegion>
);

const useRouteStep = (): WizardStep => {
  const step = useMatch(`${ROUTES.REELS_CREATOR}/${ROUTE_PATTERNS.REELS_CREATOR_SESSION}`)?.params
    .step;
  return WIZARD_STEPS.find((known) => known === step) ?? 'brief';
};

/**
 * The wizard shell's panels before the page has loaded, for the step in the route: the column
 * header, the step bubbles, the column, the main pane and, on a step that has one, the right
 * panel, so the shell replaces it without a shift. Write access is not known this early, so the
 * storyboard's action is left out of the panel rather than drawn for a member who never gets it.
 */
export const ReelsWizardSkeleton = () => {
  const currentStep = useRouteStep();

  if (currentStep === 'studio') return <ReelsStudioSkeleton />;

  return (
    <LoadingRegion data-testid="reels-wizard-skeleton" className={WIZARD_PANELS.body}>
      <div className={clsx(WIZARD_PANELS.columnSlot, 'mr-app-inset')}>
        <div data-panel="column" className={WIZARD_PANELS.column}>
          <div className={WIZARD_PANELS.columnHeader}>
            <Skeleton className="size-8" />
            <div className="flex flex-1 justify-center">
              <Skeleton className="h-5 w-24" />
            </div>
          </div>
          <div className={WIZARD_PANELS.stepsStrip}>
            {NUMBERED_STEPS.map((step, index) => (
              <div key={step} className="flex items-center">
                <div className={WIZARD_PANELS.stepBubble}>
                  <Skeleton className={WIZARD_PANELS.stepNumber} />
                  {step === currentStep ? <Skeleton className="h-4 w-16" /> : null}
                </div>
                {index < NUMBERED_STEPS.length - 1 ? (
                  <span className={WIZARD_PANELS.stepSeparator} />
                ) : null}
              </div>
            ))}
          </div>
          <div className={WIZARD_PANELS.columnBody}>
            <ColumnPlaceholders />
          </div>
        </div>
      </div>
      <div className={WIZARD_PANELS.stage}>
        <div data-panel="main" className={WIZARD_PANELS.main}>
          <div className={WIZARD_PANELS.mainBar}>
            <Skeleton className="size-8" />
            <Skeleton className="h-5 w-40" />
          </div>
          <div className={CANVAS_CLASS}>
            <CanvasPlaceholders />
          </div>
        </div>
        {WIZARD_STEPS_WITH_ASIDE.includes(currentStep) ? (
          <div className={clsx(WIZARD_PANELS.asideSlot, 'ml-app-inset')}>
            <div
              data-testid="reels-wizard-skeleton-aside"
              data-panel="aside"
              className={WIZARD_PANELS.aside}
            >
              <AsidePlaceholders hasAction={false} />
            </div>
          </div>
        ) : null}
      </div>
    </LoadingRegion>
  );
};
